import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type {
  WorkPathTask,
  WorkPathVerification,
  WorkPathVerificationType,
} from '@/hooks/useWorkPaths';

interface Props {
  tasks: WorkPathTask[];
  onChange: (tasks: WorkPathTask[]) => void;
}

let draftIdCounter = 0;
function nextDraftId(prefix: string) {
  return `${prefix}-${Date.now()}-${++draftIdCounter}`;
}

const VERIFICATION_TYPES: WorkPathVerificationType[] = [
  'checkbox',
  'text',
  'photo',
  'numeric',
  // #2378 — rates a part rather than ticking it off; what a service report
  // needs in order to say anything about the car's condition.
  'condition',
];

export function TaskEditor({ tasks, onChange }: Props) {
  const { t } = useTranslation('workpaths');

  const withSortOrder = (next: WorkPathTask[]) =>
    next.map((task, i) => ({ ...task, sortOrder: i }));

  const handleAddTask = () => {
    onChange([
      ...tasks,
      {
        id: nextDraftId('task'),
        name: '',
        description: '',
        required: false,
        sortOrder: tasks.length,
        verifications: [],
      },
    ]);
  };

  const handleRemoveTask = (index: number) => {
    onChange(withSortOrder(tasks.filter((_, i) => i !== index)));
  };

  const handleTaskChange = (
    index: number,
    field: 'name' | 'description' | 'required',
    value: unknown,
  ) => {
    const updated = [...tasks];
    const task = { ...updated[index]! };
    if (field === 'required') {
      task.required = value as boolean;
    } else {
      (task as Record<string, unknown>)[field] = value;
    }
    updated[index] = task;
    onChange(updated);
  };

  const handleMoveTask = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= tasks.length) return;
    const updated = [...tasks];
    [updated[index], updated[target]] = [updated[target]!, updated[index]!];
    onChange(withSortOrder(updated));
  };

  const updateVerifications = (
    taskIndex: number,
    verifications: WorkPathVerification[],
  ) => {
    const updated = [...tasks];
    updated[taskIndex] = {
      ...updated[taskIndex]!,
      verifications: verifications.map((v, i) => ({ ...v, sortOrder: i })),
    };
    onChange(updated);
  };

  const handleAddVerification = (taskIndex: number) => {
    const task = tasks[taskIndex]!;
    updateVerifications(taskIndex, [
      ...task.verifications,
      {
        id: nextDraftId('verify'),
        label: '',
        type: 'checkbox',
        required: false,
        // Matches the column default: a new line is shown unless someone
        // decides otherwise, so nothing goes missing from a report by accident.
        showInReport: true,
        sortOrder: task.verifications.length,
      },
    ]);
  };

  const handleRemoveVerification = (taskIndex: number, vIndex: number) => {
    const task = tasks[taskIndex]!;
    updateVerifications(
      taskIndex,
      task.verifications.filter((_, i) => i !== vIndex),
    );
  };

  const handleVerificationChange = (
    taskIndex: number,
    vIndex: number,
    field: 'label' | 'type' | 'required' | 'unit' | 'showInReport',
    value: unknown,
  ) => {
    const task = tasks[taskIndex]!;
    const verifications = [...task.verifications];
    const verification = { ...verifications[vIndex]! };
    if (field === 'required') {
      verification.required = value as boolean;
    } else if (field === 'showInReport') {
      verification.showInReport = value as boolean;
    } else if (field === 'unit') {
      verification.unit = (value as string) || null;
    } else {
      (verification as Record<string, unknown>)[field] = value;
    }
    verifications[vIndex] = verification;
    updateVerifications(taskIndex, verifications);
  };

  return (
    <Stack spacing={2}>
      {tasks.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          {t('template.noTasks')}
        </Typography>
      )}

      {tasks.map((task, index) => (
        <Card key={task.id} variant="outlined">
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack spacing={1.5}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  label={t('task.name')}
                  value={task.name}
                  onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={task.required}
                      onChange={(e) =>
                        handleTaskChange(index, 'required', e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label={t('task.required')}
                  sx={{ whiteSpace: 'nowrap' }}
                />
              </Box>

              <TextField
                label={t('task.description')}
                value={task.description ?? ''}
                onChange={(e) =>
                  handleTaskChange(index, 'description', e.target.value)
                }
                size="small"
                fullWidth
              />

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  {t('task.verifications')}
                </Typography>
              </Divider>

              {task.verifications.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('task.noVerifications')}
                </Typography>
              )}

              <Stack spacing={1}>
                {task.verifications.map((verification, vIndex) => (
                  <Box key={verification.id} display="flex" gap={1} alignItems="center">
                    <TextField
                      label={t('verification.label')}
                      value={verification.label}
                      onChange={(e) =>
                        handleVerificationChange(
                          index,
                          vIndex,
                          'label',
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      required
                    />
                    <TextField
                      select
                      label={t('verification.type')}
                      value={verification.type}
                      onChange={(e) =>
                        handleVerificationChange(
                          index,
                          vIndex,
                          'type',
                          e.target.value,
                        )
                      }
                      size="small"
                      sx={{ minWidth: 130 }}
                    >
                      {VERIFICATION_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {t(`verification.types.${type}`)}
                        </MenuItem>
                      ))}
                    </TextField>
                    {verification.type === 'numeric' && (
                      <TextField
                        label={t('verification.unit')}
                        value={verification.unit ?? ''}
                        onChange={(e) =>
                          handleVerificationChange(
                            index,
                            vIndex,
                            'unit',
                            e.target.value,
                          )
                        }
                        size="small"
                        sx={{ width: 100 }}
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={verification.required}
                          onChange={(e) =>
                            handleVerificationChange(
                              index,
                              vIndex,
                              'required',
                              e.target.checked,
                            )
                          }
                          size="small"
                        />
                      }
                      label={t('verification.required')}
                      sx={{ whiteSpace: 'nowrap' }}
                    />
                    {/* The flag the whole two-audience model turns on (#2388):
                        ticked lines reach the customer's service report,
                        unticked ones stay in the internal quality record. */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={verification.showInReport ?? true}
                          onChange={(e) =>
                            handleVerificationChange(
                              index,
                              vIndex,
                              'showInReport',
                              e.target.checked,
                            )
                          }
                          size="small"
                        />
                      }
                      label={t('verification.showInReport')}
                      title={t('verification.showInReportHint')}
                      sx={{ whiteSpace: 'nowrap' }}
                    />
                    <Tooltip title={t('verification.remove')}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveVerification(index, vIndex)}
                        color="error"
                        aria-label={t('verification.remove')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>

              <Box>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleAddVerification(index)}
                  size="small"
                >
                  {t('task.addVerification')}
                </Button>
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={0.5}>
                <Tooltip title={t('task.moveUp')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveTask(index, -1)}
                      disabled={index === 0}
                      aria-label={t('task.moveUp')}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('task.moveDown')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleMoveTask(index, 1)}
                      disabled={index === tasks.length - 1}
                      aria-label={t('task.moveDown')}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('task.remove')}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveTask(index)}
                    color="error"
                    aria-label={t('task.remove')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Button startIcon={<AddIcon />} onClick={handleAddTask} variant="outlined" size="small">
        {t('template.addTask')}
      </Button>
    </Stack>
  );
}
